import { useCallback, useState } from "react";

import { isCredentialCachingAvailable } from "@/helpers/electron-helper";

/**
 * Status of an account caching attempt.
 */
export type CacheStatus = "idle" | "loading" | "success" | "error";

export interface UseCacheDomainCredentialsReturn {
  /**
   * Trigger an account caching attempt for the given alias and password.
   * Resolves to true on success and false on failure. Never throws so callers
   * can rely on the returned status/flags.
   */
  cacheCredentials: (alias: string, password: string) => Promise<boolean>;

  /**
   * The current status of the caching attempt.
   */
  status: CacheStatus;

  /**
   * True while a caching attempt is in flight.
   */
  isLoading: boolean;

  /**
   * True once a caching attempt has succeeded.
   */
  isSuccess: boolean;

  /**
   * A non-sensitive error reason when the last attempt failed, otherwise null.
   */
  error: string | null;
}

/**
 * Hook that wraps the Electron `cacheDomainCredentials` bridge to cache the new
 * hire's ANT domain credentials on the local Windows machine.
 *
 * The password is passed straight to the Electron main process and is never
 * stored or logged by this hook.
 */
export const useCacheDomainCredentials =
  (): UseCacheDomainCredentialsReturn => {
    const [status, setStatus] = useState<CacheStatus>("idle");
    const [error, setError] = useState<string | null>(null);

    const cacheCredentials = useCallback(
      async (alias: string, password: string): Promise<boolean> => {
        setStatus("loading");
        setError(null);

        const cacheFn = window.electronAPI?.cacheDomainCredentials;
        if (!isCredentialCachingAvailable() || !cacheFn) {
          setStatus("error");
          setError("Account caching is not available in this environment.");
          return false;
        }

        try {
          const result = await cacheFn(alias, password);

          if (result.success) {
            setStatus("success");
            return true;
          }

          setStatus("error");
          setError(result.error ?? "Failed to cache credentials.");
          return false;
        } catch (err) {
          setStatus("error");
          setError(
            err instanceof Error ? err.message : "Failed to cache credentials."
          );
          return false;
        }
      },
      []
    );

    return {
      cacheCredentials,
      status,
      isLoading: status === "loading",
      isSuccess: status === "success",
      error,
    };
  };
